import { useState, useEffect, useRef } from "preact/hooks";
import { useRange, useProductFilters } from "@nosto/search-js/preact/hooks";
import RangeInput from "@/elements/RangeInput/RangeInput";
import { SearchStatsFacet } from "@nosto/nosto-js/client";
import { useDebounce } from "@/hooks/useDebounce";

type Props = {
  facet: SearchStatsFacet;
};

export default function RangeFacet({ facet }: Props) {
  const { min, max, updateRange } = useRange(facet.id);
  const { filters } = useProductFilters();

  const [open, setOpen] = useState<boolean>(true);
  const [localMin, setLocalMin] = useState<number | "">("");
  const [localMax, setLocalMax] = useState<number | "">("");
  const [isMinFocused, setIsMinFocused] = useState(false);
  const [isMaxFocused, setIsMaxFocused] = useState(false);

  const debouncedMin = useDebounce(localMin, 2000);
  const debouncedMax = useDebounce(localMax, 2000);

  const hasInteracted = useRef(false);
  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);

  const priceIsFiltered = filters.some((filter) => filter?.filter.range);

  const toggleOpen = () => setOpen((prev) => !prev);

  const applyFilter = () => {
    if (debouncedMax === "") return;
    if (isMinFocused && localMin === "") return;

    const finalMin = debouncedMin === "" ? 0 : Number(debouncedMin);
    const finalMax = Number(debouncedMax);

    updateRange([finalMin, finalMax]);
    minInputRef.current?.blur();
    maxInputRef.current?.blur();
  };

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasInteracted.current = true;
    const val = e.currentTarget.value;
    setLocalMin(val === "" ? "" : Number(val));
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasInteracted.current = true;
    const val = e.currentTarget.value;
    setLocalMax(val === "" ? "" : Number(val));
  };

  // Reset local state when filters are cleared externally
  useEffect(() => {
    if (!priceIsFiltered) {
      setLocalMin("");
      setLocalMax("");
      hasInteracted.current = false;
    }
  }, [priceIsFiltered]);

  useEffect(() => {
    if (!hasInteracted.current) return;

    applyFilter();
  }, [debouncedMin, debouncedMax, isMinFocused, isMaxFocused]);

  return (
    <div
      className={`mobile-filter-facets__nav-item ${open ? "mobile-filter-facets__nav-item--is-open" : ""}`}
    >
      <button onClick={toggleOpen} aria-expanded={open} type="button">
        <span>{facet.name}</span>
      </button>

      <div
        className="mobile-filter-facets__nav-item-options"
        id={`${facet.id}-sub-menu`}
      >
        <div className="price-range-filter">
          <div className="price-range-filter__input br-input">
            <label htmlFor="mobile-min-price">Min</label>
            <RangeInput
              ref={minInputRef}
              id="mobile-min-price"
              value={localMin ?? ""}
              min={min}
              max={max}
              placeholder="0"
              onChange={handleMinChange}
              onFocus={() => {
                setIsMinFocused(true);
                setLocalMin("");
              }}
              onBlur={() => setIsMinFocused(false)}
              className="br-input__control"
            />
          </div>
          <div className="price-range-filter__input br-input">
            <label htmlFor="mobile-max-price">Max</label>
            <RangeInput
              ref={maxInputRef}
              id="mobile-max-price"
              value={localMax ?? ""}
              min={min}
              max={max}
              onChange={handleMaxChange}
              onFocus={() => {
                setIsMaxFocused(true);
                setLocalMax("");
              }}
              onBlur={() => setIsMaxFocused(false)}
              className="br-input__control"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
